import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BrandedProductName } from '../components/BrandedProductName';
import { 
  ArrowLeft, 
  ArrowRight,
  Users, 
  Eye, 
  Shield, 
  Sparkles, 
  Layers,
  Target,
  Lightbulb,
  Network,
  TrendingUp
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  const heroRef = useRef(null);
  const missionRef = useRef(null);
  const prioritiesRef = useRef(null);

  useEffect(() => {
    // Animate sections on scroll
    const sections = document.querySelectorAll('.about-section');
    sections.forEach((section) => {
      gsap.fromTo(
        section,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const priorities = [
    { icon: Users, title: "Athlete-First Design", description: "Every feature built with the athlete's journey in mind" },
    { icon: Eye, title: "Department-Wide Visibility", description: "Complete transparency across your organization" },
    { icon: Shield, title: "Role-Based Access", description: "Security and accountability at every level" },
    { icon: Sparkles, title: "Modern Experience", description: "Clean, intuitive interfaces that teams love" },
    { icon: Layers, title: "Scalable Architecture", description: "Future-proof technology that grows with you" }
  ];

  const futurePoints = [
    { icon: Lightbulb, text: "Better information" },
    { icon: Network, text: "Better collaboration" },
    { icon: TrendingUp, text: "Better infrastructure" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32"
      >
        <div className="grid-pattern" />
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#D3FF62]/10 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-[#004534]/20 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="container relative z-10 text-center max-w-6xl mx-auto px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-6"
          >
            About Us
          </motion.p>
          
          {/* Hero text matching homepage style */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-2"
          >
            <h1 className="text-[clamp(2rem,8vw,6rem)] font-black text-white leading-none tracking-[-0.04em] uppercase">
              BUILT AT THE
            </h1>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-2"
          >
            <h1 
              className="text-[clamp(2rem,8vw,6rem)] font-black leading-none tracking-[-0.04em] uppercase"
              style={{ 
                WebkitTextStroke: '2px #D3FF62',
                WebkitTextFillColor: 'transparent'
              }}
            >
              INTERSECTION
            </h1>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-[clamp(2rem,8vw,6rem)] font-black gradient-text leading-none tracking-[-0.04em] uppercase">
              OF SPORTS & TECH
            </h1>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="body-large text-white/70 max-w-3xl mx-auto"
          >
            Omni Sports Group is a sports-technology company building a connected ecosystem of platforms designed to support the modern athlete and the organizations around them.
          </motion.p>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="section about-section">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="p-8 md:p-12 rounded-3xl bg-[#141414] border border-white/10">
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-6">
              Across high school, collegiate, and professional sports, athlete information is fragmented—spread across spreadsheets, disconnected software, emails, and manual processes.
            </p>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              <span className="text-white font-semibold">Omni Sports Group exists to eliminate that fragmentation</span> by unifying athlete data, evaluation, development, recruiting, and representation into modern, purpose-built technology.
            </p>
          </div>
        </div>
      </section>

      {/* Big Quote Section */}
      <section className="section about-section bg-[#D3FF62] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[15vw] font-black text-[#004534]/5 whitespace-nowrap">
            LIFECYCLE
          </span>
        </div>
        
        <div className="container relative z-10 max-w-5xl mx-auto px-4 text-center">
          <p className="text-[#004534]/60 text-lg mb-4">Our Approach</p>
          <h2 className="text-[clamp(1.5rem,4vw,3rem)] font-bold text-[#004534] leading-tight mb-4">
            We don't build tools for one moment in an athlete's journey.
          </h2>
          <h2 className="text-[clamp(1.5rem,4vw,3rem)] font-bold text-[#004534] leading-tight">
            We build <span className="underline decoration-4 decoration-[#004534]/30">infrastructure for the entire lifecycle.</span>
          </h2>
        </div>
      </section>

      {/* Mission Section */}
      <section ref={missionRef} className="section about-section">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
              Our Mission
            </p>
            <h2 className="display-medium text-white mb-6">
              Clarity. Consistency. Intelligence.
            </h2>
          </div>
          
          <p className="text-white/80 text-lg md:text-xl leading-relaxed text-center mb-8">
            Our mission is to bring clarity, consistency, and intelligence to sports by creating software that connects people, data, and decisions across every level of athletics.
          </p>
          
          <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center">
            <p className="text-white/90 text-lg italic">
              "We believe better systems lead to better outcomes—for athletes, coaches, agents, and organizations alike."
            </p>
          </div>
        </div>
      </section>

      {/* Built on Real-World Experience Section */}
      <section className="section about-section">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
                Our Foundation
              </p>
              <h2 className="display-medium text-white mb-6">
                Built on Real-World Experience
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-6">
                Omni Sports Group was founded by a former NFL personnel executive with over 20 years of experience evaluating players, managing rosters, and operating inside professional sports organizations.
              </p>
              <p className="text-white/80 text-lg leading-relaxed">
                That experience shapes every platform we build—ensuring our technology reflects real workflows, real accountability, and real decision-making environments across high school, collegiate, and professional sports.
              </p>
            </div>
            
            {/* Visual */}
            <div className="relative">
              <div className="p-8 rounded-3xl bg-[#141414] border border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#D3FF62] flex items-center justify-center">
                    <span className="text-[#004534] text-2xl font-black">20+</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Years of Experience</p>
                    <p className="text-white/60 text-sm">In Professional Sports</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D3FF62]" />
                    <span className="text-white/80">NFL Personnel Executive Background</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D3FF62]" />
                    <span className="text-white/80">Player Evaluation Expertise</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D3FF62]" />
                    <span className="text-white/80">Roster Management Experience</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D3FF62]" />
                    <span className="text-white/80">Real-World Workflow Knowledge</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Build Section */}
      <section className="section about-section bg-[#0F0F0F]">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
              What We Build
            </p>
            <h2 className="display-medium text-white mb-6">
              Our Ventures
            </h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              OmniSportsGroup is the parent company behind a growing portfolio of sports technology platforms and intelligence services.
            </p>
          </div>

          <div className="space-y-6">
            {/* OMNIAGENCY - Featured */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-3xl bg-[#D3FF62] text-[#004534]"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-[#004534] text-[#D3FF62] text-xs font-bold">AVAILABLE NOW</span>
              </div>
              <h3 className="text-2xl font-bold mb-3"><BrandedProductName product="OMNIAGENCY" /></h3>
              <p className="text-[#004534]/80 leading-relaxed">
                The modern platform for sports agents and collectives to manage athletes, collaborate across teams, and streamline NIL deals, contracts, and partnerships all in one place.
              </p>
              <Link to="/omniagency" className="inline-flex items-center gap-2 mt-4 font-semibold hover:gap-3 transition-all">
                Explore Now <ArrowRight size={18} />
              </Link>
            </motion.div>

            {/* VERIFIED */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-3xl bg-[#141414] border border-white/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(0,229,255,0.15)', color: '#00E5FF' }}>AVAILABLE NOW</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-1"><BrandedProductName product="VERIFIED" /></h3>
              <p className="text-sm text-white/40 mb-3">Powered by Omni</p>
              <p className="text-white/70 leading-relaxed">
                A premium athlete verification and recruiting-intelligence platform that turns real, measured performance into trusted, shareable proof — so high-school athletes get seen by the right people.
              </p>
              <a href="https://verifiedbyomni.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 font-semibold text-white hover:gap-3 transition-all" style={{ color: '#00E5FF' }}>
                Visit Verified <ArrowRight size={18} />
              </a>
            </motion.div>

            {/* THE DOSSIER */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-3xl bg-[#141414] border border-white/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>FOUNDING</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">THE DOSSIER</h3>
              <p className="text-white/70 leading-relaxed">
                Weekly evaluations of NFL draft prospects and transfer-portal players, written by a former NFL scout with twenty-plus years across Combine, Pro, and College scouting. Three franchises. Three regions. One voice.
              </p>
              <a href="https://getthedossier.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 font-semibold hover:gap-3 transition-all" style={{ color: '#FFD700' }}>
                Visit The Dossier <ArrowRight size={18} />
              </a>
            </motion.div>

            {/* OMNIATHLETICS */}
            <div className="p-8 rounded-3xl bg-[#141414] border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-semibold">COMING SOON</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3"><BrandedProductName product="OMNIATHLETICS" /></h3>
              <p className="text-white/70 leading-relaxed">
                A platform purpose-built for high school athletic departments. Manage rosters, track eligibility, coordinate across sports, and give coaches and administrators the tools they need to run programs efficiently.
              </p>
            </div>

            {/* OMNIDRAFTLAB */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-3xl bg-[#141414] border border-white/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(160,160,160,0.15)', color: '#A0A0A0' }}>APPLICATION ONLY</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3"><BrandedProductName product="OMNIDRAFTLAB" /></h3>
              <p className="text-white/70 leading-relaxed">
                A private, application-only advisory service that prepares draft-eligible athletes and their families for how the NFL actually works. Front-office-grade evaluation, strategy, and positioning — built on twenty years of NFL personnel experience.
              </p>
              <a href="https://omnidraftlab.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 font-semibold text-white/70 hover:text-white hover:gap-3 transition-all">
                Visit OmniDraftLab <ArrowRight size={18} />
              </a>
            </motion.div>

            {/* OMNISCOUTING */}
            <div className="p-8 rounded-3xl bg-[#141414] border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-semibold">COMING SOON</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3"><BrandedProductName product="OMNISCOUTING" /></h3>
              <p className="text-white/70 leading-relaxed">
                A unified platform that connects every department within an athletic organization — from coaches and players to executives and families. Centralize athlete data, enhance collaboration, and streamline operations across professional teams, college programs, and high school athletic departments.
              </p>
            </div>
          </div>

          <p className="text-center text-white/60 mt-12 text-lg">
            Each venture is purpose-built for its audience — together they represent our commitment to modernizing every corner of the sports industry.
          </p>
        </div>
      </section>

      {/* Why Omni Section */}
      <section ref={prioritiesRef} className="section about-section">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
              Why Omni
            </p>
            <h2 className="display-medium text-white mb-6">
              Built Differently
            </h2>
            <p className="body-large text-white/70 max-w-3xl mx-auto">
              Our platforms are shaped by real experience inside sports organizations—not theory or generic software assumptions. We focus on workflows that actually exist, information that actually matters, and tools that scale as athletes progress.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {priorities.map((priority, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-[#141414] border border-white/10 hover:border-[#D3FF62]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D3FF62]/10 flex items-center justify-center mb-4">
                  <priority.icon className="text-[#D3FF62]" size={24} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{priority.title}</h3>
                <p className="text-white/60 text-sm">{priority.description}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-white/80 mt-12 text-xl font-medium">
            We build technology that professionals trust.
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section about-section bg-[#0F0F0F]">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
            Our Philosophy
          </p>
          <h2 className="display-medium text-white mb-8">
            Sports Evolve. Systems Must Too.
          </h2>
          
          <p className="text-white/70 text-lg mb-12">
            Omni Sports Group believes the future of athletics depends on:
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
            {futurePoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.15 }}
                className="flex items-center gap-3 px-6 py-4 rounded-full bg-white/5 border border-white/10"
              >
                <point.icon className="text-[#D3FF62]" size={20} />
                <span className="text-white font-medium">{point.text}</span>
              </motion.div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-[#D3FF62]/10 border border-[#D3FF62]/20">
            <p className="text-white text-xl md:text-2xl font-medium leading-relaxed">
              Our goal is simple: <span className="text-[#D3FF62]">to power smarter decisions and stronger outcomes</span> through connected sports technology.
            </p>
          </div>
        </div>
      </section>

      {/* Looking Ahead Section */}
      <section className="section about-section">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
            Looking Ahead
          </p>
          <h2 className="display-medium text-white mb-8">
            Building for the Future of Sport
          </h2>
          
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            As the sports landscape continues to change—through NIL, the transfer portal, data analytics, and emerging athlete pathways—Omni Sports Group will continue to expand its platform ecosystem to meet the needs of athletes and organizations at every level.
          </p>

          <p className="text-white font-semibold text-xl">
            We are building for today, with a long-term view toward the future of sport.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section about-section">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="display-medium text-white mb-6">
            Ready to Join Us?
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Whether you're an organization looking to modernize or an athlete ready to take control of your future, we'd love to connect.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/" 
              onClick={() => {
                setTimeout(() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className="btn-primary text-lg px-8 py-4"
            >
              Get in Touch
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/omniagency" 
              onClick={() => window.scrollTo(0, 0)}
              className="btn-secondary text-lg px-8 py-4"
            >
              Explore <BrandedProductName product="OMNIAGENCY" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#D3FF62] flex items-center justify-center">
                <span className="text-[#004534] font-bold">O</span>
              </div>
              <span className="text-white text-lg tracking-tight">
                <span className="font-medium">OMNI</span>
                <span className="font-black text-[#D3FF62]">SPORTS</span>
                <span className="font-medium">GROUP</span>
              </span>
            </Link>
            <p className="text-white/50 text-sm">
              © 2025 OmniSportsGroup. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
